import React, { useEffect, useState } from "react";
import { Save, Upload, Plus, Trash2 } from "lucide-react";
import { api, fileUrl } from "../../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../hooks/use-toast";
import { useSite } from "../../context/SiteContext";

export default function AdminSettings() {
  const { toast } = useToast();
  const { refresh } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="text-slate-400">Loading settings...</div>;

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setSeo = (k, v) => setS((x) => ({ ...x, defaultSeo: { ...(x.defaultSeo || {}), [k]: v } }));
  const setArr = (key, idx, k, v) => setS((x) => {
    const arr = [...(x[key] || [])]; arr[idx] = { ...arr[idx], [k]: v }; return { ...x, [key]: arr };
  });
  const addItem = (key, item) => setS((x) => ({ ...x, [key]: [...(x[key] || []), item] }));
  const delItem = (key, idx) => setS((x) => ({ ...x, [key]: (x[key] || []).filter((_, i) => i !== idx) }));

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logoUrl", data.url);
      toast({ title: "Logo uploaded" });
    } catch { toast({ title: "Upload failed" }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/settings", s);
      await refresh();
      toast({ title: "Settings saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Site & SEO Settings</h1>
          <p className="text-slate-400 text-sm mt-1">Edit brand, logo, contact info, home page sections and SEO defaults.</p>
        </div>
        <Button onClick={save} disabled={saving} className="btn-gold border-0 px-5 py-2.5 rounded-lg"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save All"}</Button>
      </div>

      <Tabs defaultValue="brand" className="mt-6">
        <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="brand">Brand & Logo</TabsTrigger>
          <TabsTrigger value="contact">Contact & Social</TabsTrigger>
          <TabsTrigger value="home">Home Page</TabsTrigger>
          <TabsTrigger value="exchanges">Exchanges</TabsTrigger>
          <TabsTrigger value="seo">Default SEO</TabsTrigger>
          <TabsTrigger value="robots">robots.txt</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="mt-5">
          <div className="card-dark p-5 grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Brand Name</Label>
              <Input value={s.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div>
              <Label>Domain</Label>
              <Input value={s.domain} onChange={(e) => set("domain", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div className="sm:col-span-2">
              <Label>Tagline</Label>
              <Input value={s.tagline} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div>
              <Label>Since (year)</Label>
              <Input type="number" value={s.since} onChange={(e) => set("since", parseInt(e.target.value || 2015))} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div>
              <Label>Users Count Label</Label>
              <Input value={s.users} onChange={(e) => set("users", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" />
            </div>
            <div className="sm:col-span-2">
              <Label>Logo</Label>
              {s.logoUrl && <img src={fileUrl(s.logoUrl)} alt="logo" className="mt-2 h-16 object-contain bg-white/5 p-2 rounded border border-white/10" />}
              <div className="flex gap-2 items-center mt-2">
                <Input value={s.logoUrl} onChange={(e) => set("logoUrl", e.target.value)} placeholder="Logo URL or upload" className="bg-[#0c1530] border-white/10 text-white text-xs" />
                <label className="btn-outline-gold cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs">
                  <Upload className="w-3.5 h-3.5" /> Upload
                  <input type="file" accept="image/*" onChange={onUploadLogo} className="hidden" />
                </label>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Leave blank to use the default Trophy icon.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contact" className="mt-5">
          <div className="card-dark p-5 grid sm:grid-cols-2 gap-4">
            <div><Label>WhatsApp URL</Label><Input value={s.whatsappUrl} onChange={(e) => set("whatsappUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Telegram URL</Label><Input value={s.telegramUrl} onChange={(e) => set("telegramUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Email</Label><Input value={s.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Phone</Label><Input value={s.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Facebook URL</Label><Input value={s.facebookUrl} onChange={(e) => set("facebookUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Instagram URL</Label><Input value={s.instagramUrl} onChange={(e) => set("instagramUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Twitter / X URL</Label><Input value={s.twitterUrl} onChange={(e) => set("twitterUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>LinkedIn URL</Label><Input value={s.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
          </div>
        </TabsContent>

        <TabsContent value="home" className="mt-5 space-y-5">
          <div className="card-dark p-5 space-y-4">
            <h3 className="font-bold text-white">Hero Section</h3>
            <div><Label>Hero Badge</Label><Input value={s.heroBadge} onChange={(e) => set("heroBadge", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Hero Title</Label><Input value={s.heroTitle} onChange={(e) => set("heroTitle", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Hero Subtitle</Label><Textarea rows={2} value={s.heroSubtitle} onChange={(e) => set("heroSubtitle", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
          </div>

          <div className="card-dark p-5">
            <div className="flex items-center justify-between"><h3 className="font-bold text-white">Stats</h3>
              <button onClick={() => addItem("stats", { value: "100+", label: "New" })} className="text-xs text-amber-400 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="mt-3 space-y-2">
              {(s.stats || []).map((st, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                  <Input value={st.value} onChange={(e) => setArr("stats", i, "value", e.target.value)} placeholder="value" className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={st.label} onChange={(e) => setArr("stats", i, "label", e.target.value)} placeholder="label" className="bg-[#0c1530] border-white/10 text-white" />
                  <button onClick={() => delItem("stats", i)} className="text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="card-dark p-5">
            <div className="flex items-center justify-between"><h3 className="font-bold text-white">Features</h3>
              <button onClick={() => addItem("features", { icon: "Zap", title: "New Feature", desc: "Description" })} className="text-xs text-amber-400 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="mt-3 space-y-3">
              {(s.features || []).map((f, i) => (
                <div key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/5 grid sm:grid-cols-[140px_1fr_2fr_auto] gap-2 items-center">
                  <Input value={f.icon} onChange={(e) => setArr("features", i, "icon", e.target.value)} placeholder="icon (Zap, ShieldCheck...)" className="bg-[#0c1530] border-white/10 text-white text-xs" />
                  <Input value={f.title} onChange={(e) => setArr("features", i, "title", e.target.value)} placeholder="title" className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={f.desc} onChange={(e) => setArr("features", i, "desc", e.target.value)} placeholder="desc" className="bg-[#0c1530] border-white/10 text-white" />
                  <button onClick={() => delItem("features", i)} className="text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Available icons: Zap, ShieldCheck, Wallet, Trophy, Headphones, BadgePercent, Activity, Spade, Plane, Users, BarChart3</div>
          </div>

          <div className="card-dark p-5">
            <div className="flex items-center justify-between"><h3 className="font-bold text-white">Steps</h3>
              <button onClick={() => addItem("steps", { n: "0X", title: "Step", desc: "Description" })} className="text-xs text-amber-400 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="mt-3 space-y-3">
              {(s.steps || []).map((f, i) => (
                <div key={i} className="grid sm:grid-cols-[60px_1fr_2fr_auto] gap-2 items-center">
                  <Input value={f.n} onChange={(e) => setArr("steps", i, "n", e.target.value)} className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={f.title} onChange={(e) => setArr("steps", i, "title", e.target.value)} className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={f.desc} onChange={(e) => setArr("steps", i, "desc", e.target.value)} className="bg-[#0c1530] border-white/10 text-white" />
                  <button onClick={() => delItem("steps", i)} className="text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="exchanges" className="mt-5">
          <div className="card-dark p-5">
            <div className="flex items-center justify-between"><h3 className="font-bold text-white">Exchange Partners</h3>
              <button onClick={() => addItem("exchanges", { name: "NEW.COM", user: "", pass: "", bonus: "5% Welcome Bonus", commission: "10% Loss Commission" })} className="text-xs text-amber-400 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="mt-3 space-y-3">
              {(s.exchanges || []).map((x, i) => (
                <div key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/5 grid sm:grid-cols-5 gap-2 items-center">
                  <Input value={x.name} onChange={(e) => setArr("exchanges", i, "name", e.target.value)} placeholder="Name" className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={x.user} onChange={(e) => setArr("exchanges", i, "user", e.target.value)} placeholder="Demo user" className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={x.pass} onChange={(e) => setArr("exchanges", i, "pass", e.target.value)} placeholder="Demo pass" className="bg-[#0c1530] border-white/10 text-white" />
                  <Input value={x.bonus} onChange={(e) => setArr("exchanges", i, "bonus", e.target.value)} placeholder="Bonus" className="bg-[#0c1530] border-white/10 text-white" />
                  <div className="flex gap-2">
                    <Input value={x.commission} onChange={(e) => setArr("exchanges", i, "commission", e.target.value)} placeholder="Commission" className="bg-[#0c1530] border-white/10 text-white" />
                    <button onClick={() => delItem("exchanges", i)} className="text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-5">
          <div className="card-dark p-5 space-y-4">
            <h3 className="font-bold text-white">Default SEO (used on home + fallback)</h3>
            <div><Label>Default Title</Label><Input value={s.defaultSeo?.title || ""} onChange={(e) => setSeo("title", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Default Description</Label><Textarea rows={2} value={s.defaultSeo?.description || ""} onChange={(e) => setSeo("description", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Default Keywords</Label><Input value={s.defaultSeo?.keywords || ""} onChange={(e) => setSeo("keywords", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Default OG Image URL</Label><Input value={s.defaultSeo?.ogImage || ""} onChange={(e) => setSeo("ogImage", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Canonical Origin (e.g. https://onlinecricketid.guru)</Label><Input value={s.defaultSeo?.canonical || ""} onChange={(e) => setSeo("canonical", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white" /></div>
            <div><Label>Global JSON-LD (Organization schema)</Label><Textarea rows={6} value={s.defaultSeo?.jsonLd || ""} onChange={(e) => setSeo("jsonLd", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-xs" /></div>
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-200">Sitemap auto-generated at <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="underline">/api/sitemap.xml</a> from your published pages + posts.</div>
          </div>
        </TabsContent>

        <TabsContent value="robots" className="mt-5">
          <div className="card-dark p-5">
            <Label>robots.txt content</Label>
            <Textarea rows={10} value={s.robotsTxt || ""} onChange={(e) => set("robotsTxt", e.target.value)} className="mt-1.5 bg-[#0c1530] border-white/10 text-white font-mono text-sm" />
            <div className="mt-2 text-xs text-slate-400">Available at <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="text-amber-400">/api/robots.txt</a></div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
